/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import Footer from '../components/Footer';

const STATUS_COLORS = {
    active: 'bg-success', inactive: 'bg-danger', blocked: 'bg-warning',
};
const LEAD_STATUS_COLORS = {
    new: 'bg-primary', contacted: 'bg-info', follow_up: 'bg-warning',
    qualified: 'bg-success', lost: 'bg-danger', converted: 'bg-secondary',
};
const INTERACTION_ICONS = {
    Call: 'fa-phone', WhatsApp: 'fa-whatsapp', Email: 'fa-envelope',
    Meeting: 'fa-users', Note: 'fa-sticky-note', 'Follow-up': 'fa-clock',
};
const INTERACTION_COLORS = {
    Call: '#28a745', WhatsApp: '#25D366', Email: '#007bff',
    Meeting: '#6f42c1', Note: '#fd7e14', 'Follow-up': '#20c997',
};

function CustomerDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const chatEndRef = useRef(null);

    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('details');

    // Lead data
    const [lead, setLead] = useState(null);
    const [leadLoading, setLeadLoading] = useState(false);

    // Lead Interactions
    const [interactions, setInteractions] = useState([]);
    const [interactionLoading, setInteractionLoading] = useState(false);

    // Lead Assignment Logs
    const [assignLogs, setAssignLogs] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);

    // ─── Fetch ────────────────────────────────────────────────────────────────────

    const fetchCustomer = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/api/customer/${id}`);
            const data = res.data.data || res.data;
            setCustomer(data);
            // If customer has a lead_id, fetch lead details
            if (data?.lead_id) {
                fetchLead(data.lead_id);
                fetchInteractions(data.lead_id);
                fetchAssignLogs(data.lead_id);
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to fetch customer' });
        } finally {
            setLoading(false);
        }
    };

    const fetchLead = async (leadId) => {
        try {
            setLeadLoading(true);
            const res = await axiosInstance.get(`/api/leads/${leadId}`);
            setLead(res.data.data || res.data);
        } catch { setLead(null); } finally { setLeadLoading(false); }
    };

    const fetchInteractions = async (leadId) => {
        try {
            setInteractionLoading(true);
            const res = await axiosInstance.get(`/api/leadInteractions?lead_id=${leadId}`);
            setInteractions(res.data.data || res.data || []);
        } catch { setInteractions([]); } finally { setInteractionLoading(false); }
    };

    const fetchAssignLogs = async (leadId) => {
        try {
            setAssignLoading(true);
            const res = await axiosInstance.get(`/api/leadAssign?lead_id=${leadId}`);
            setAssignLogs(res.data.data || res.data || []);
        } catch { setAssignLogs([]); } finally { setAssignLoading(false); }
    };

    useEffect(() => { fetchCustomer(); }, [id]);

    useEffect(() => {
        if (activeTab === 'interactions') {
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
    }, [activeTab, interactions]);

    // ─── Delete ───────────────────────────────────────────────────────────────────

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Delete Customer?', text: 'This action cannot be undone.', icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete!'
        });
        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/api/customer/${id}`);
                Swal.fire({ icon: 'success', title: 'Deleted!', timer: 1500, showConfirmButton: false });
                navigate('/customer');
            } catch {
                Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete customer' });
            }
        }
    };

    // ─── Helpers ─────────────────────────────────────────────────────────────────

    const InfoRow = ({ label, value }) => (
        <div className="col-md-6 mb-3">
            <label className="text-muted small mb-1 d-block">{label}</label>
            <span className="fw-medium">{value || <span className="text-muted">—</span>}</span>
        </div>
    );

    const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';
    const formatDateOnly = (d) => d ? new Date(d).toLocaleDateString() : '—';

    // ─── Loading / Not Found ──────────────────────────────────────────────────────

    if (loading) return (
        <div className="page-wrapper">
            <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status"></div>
            </div>
            <Footer />
        </div>
    );

    if (!customer) return (
        <div className="page-wrapper">
            <div className="content container-fluid"><div className="alert alert-danger">Customer not found.</div></div>
            <Footer />
        </div>
    );

    // Build tabs — only show lead tabs if customer has a lead
    const tabs = [
        { key: 'details', icon: 'fa-info-circle', label: 'Details' },
        ...(customer.lead_id ? [
            { key: 'lead', icon: 'fa-funnel-dollar', label: 'Lead Info' },
            { key: 'interactions', icon: 'fa-comments', label: `Lead Interactions (${interactions.length})` },
            { key: 'assignment', icon: 'fa-history', label: `Assignment Logs (${assignLogs.length})` },
        ] : []),
    ];

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">

                    {/* ── Header ── */}
                    <div className="page-header">
                        <div className="content-page-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center">
                                <button className="btn btn-sm btn-outline-secondary me-3" onClick={() => navigate('/customer')}>
                                    <i className="fa fa-arrow-left me-1"></i> Back
                                </button>
                                <div>
                                    <h5 className="mb-0">{customer.name}</h5>
                                    <small className="text-muted">
                                        <i className="fa fa-phone me-1"></i>{customer.phone}
                                        {customer.email && <> · <i className="fa fa-envelope me-1 ms-1"></i>{customer.email}</>}
                                    </small>
                                </div>
                                <span className={`badge ms-3 ${STATUS_COLORS[customer.status] || 'bg-secondary'}`}>
                                    {customer.status || 'N/A'}
                                </span>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                <button className="btn btn-primary btn-sm"
                                    onClick={() => navigate('/add-customer', { state: { customer, isEdit: true } })}>
                                    <i className="fa fa-edit me-1"></i> Edit
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={handleDelete}>
                                    <i className="fa fa-trash-alt me-1"></i> Delete
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Breadcrumb */}
                    <nav aria-label="breadcrumb" className="mb-3">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <span style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => navigate('/customer')}>Customers</span>
                            </li>
                            <li className="breadcrumb-item active">{customer.name}</li>
                        </ol>
                    </nav>

                    {/* ── Tabs ── */}
                    <ul className="nav nav-tabs mb-3">
                        {tabs.map(t => (
                            <li className="nav-item" key={t.key}>
                                <button className={`nav-link ${activeTab === t.key ? 'active' : ''}`}
                                    onClick={() => setActiveTab(t.key)}>
                                    <i className={`fa ${t.icon} me-2`}></i>{t.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* ── Tab: Customer Details ── */}
                    {activeTab === 'details' && (
                        <div className="card mb-0">
                            <div className="card-body">
                                <h6 className="text-primary border-bottom pb-2 mb-3">Personal Information</h6>
                                <div className="row">
                                    <InfoRow label="Full Name" value={customer.name} />
                                    <InfoRow label="Phone" value={<a href={`tel:${customer.phone}`}>{customer.phone}</a>} />
                                    <InfoRow label="Alternate Phone" value={customer.alternate_phone ? <a href={`tel:${customer.alternate_phone}`}>{customer.alternate_phone}</a> : null} />
                                    <InfoRow label="Email" value={customer.email ? <a href={`mailto:${customer.email}`} className="text-primary">{customer.email}</a> : null} />
                                    <InfoRow label="Gender" value={customer.gender} />
                                    <InfoRow label="Date of Birth" value={formatDateOnly(customer.dob)} />
                                    <InfoRow label="Status" value={
                                        <span className={`badge ${STATUS_COLORS[customer.status] || 'bg-secondary'}`}>
                                            {customer.status}
                                        </span>
                                    } />
                                    <InfoRow label="Preferred Contact" value={customer.preferred_contact_method} />
                                </div>

                                <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Location</h6>
                                <div className="row">
                                    <InfoRow label="Country" value={customer.countries?.name} />
                                    <InfoRow label="State" value={customer.states?.name} />
                                    <InfoRow label="City" value={customer.cities?.name} />
                                    <InfoRow label="Address" value={customer.address} />
                                </div>

                                <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Travel & Booking Info</h6>
                                <div className="row">
                                    <InfoRow label="Total Bookings" value={customer.total_bookings} />
                                    <InfoRow label="Total Spent" value={customer.total_spent ? `₹${Number(customer.total_spent).toLocaleString()}` : null} />
                                    <InfoRow label="Passport Number" value={customer.passport_number} />
                                    <InfoRow label="Passport Expiry" value={formatDateOnly(customer.passport_expiry_date)} />
                                </div>

                                {/* Lead Link */}
                                {customer.lead_id && (
                                    <>
                                        <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Source Lead</h6>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="text-muted small mb-1 d-block">Converted From Lead</label>
                                                <span
                                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline', fontWeight: 500 }}
                                                    onClick={() => navigate(`/lead-details/${customer.lead_id}`)}
                                                >
                                                    <i className="fa fa-external-link-alt me-1"></i>
                                                    {lead?.name || customer.lead_id}
                                                </span>
                                                {lead && (
                                                    <span className={`badge ms-2 ${LEAD_STATUS_COLORS[lead.status] || 'bg-secondary'}`}>
                                                        {lead.status?.replace('_', ' ')}
                                                    </span>
                                                )}
                                            </div>
                                            {lead && <InfoRow label="Lead Source" value={lead.source} />}
                                        </div>
                                    </>
                                )}

                                <h6 className="text-primary border-bottom pb-2 mb-3 mt-4">Record Info</h6>
                                <div className="row">
                                    <InfoRow label="Created At" value={formatDate(customer.createdAt)} />
                                    <InfoRow label="Updated At" value={formatDate(customer.updatedAt)} />
                                    <InfoRow label="Created By" value={customer.createdBy?.user_name} />
                                    <InfoRow label="Updated By" value={customer.updatedBy?.user_name} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Tab: Lead Info ── */}
                    {activeTab === 'lead' && (
                        <div className="card mb-0">
                            <div className="card-body">
                                {leadLoading ? (
                                    <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                                ) : !lead ? (
                                    <div className="text-center text-muted py-5">
                                        <i className="fa fa-funnel-dollar fa-3x mb-3 d-block"></i>
                                        Lead details not available.
                                    </div>
                                ) : (
                                    <>
                                        {/* Lead header with link */}
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <h6 className="text-primary mb-0">Lead Details</h6>
                                            <button className="btn btn-sm btn-outline-primary"
                                                onClick={() => navigate(`/lead-details/${customer.lead_id}`)}>
                                                <i className="fa fa-external-link-alt me-1"></i> Open Full Lead
                                            </button>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table table-bordered table-hover">
                                                <tbody>
                                                    <tr>
                                                        <th className="text-muted" style={{ width: '30%' }}>Lead Name</th>
                                                        <td>
                                                            <span
                                                                style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                onClick={() => navigate(`/lead-details/${customer.lead_id}`)}
                                                            >
                                                                {lead.name || '—'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Phone</th>
                                                        <td><a href={`tel:${lead.phone}`}>{lead.phone || '—'}</a></td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Email</th>
                                                        <td>{lead.email ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Source</th>
                                                        <td>{lead.source || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Status</th>
                                                        <td>
                                                            <span className={`badge ${LEAD_STATUS_COLORS[lead.status] || 'bg-secondary'}`}>
                                                                {lead.status?.replace('_', ' ') || '—'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Travel Type</th>
                                                        <td>{lead.travel_type || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Preferred Start Date</th>
                                                        <td>{lead.preferred_start_date ? new Date(lead.preferred_start_date).toLocaleDateString() : '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">No. of People</th>
                                                        <td>{lead.no_of_people || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Country</th>
                                                        <td>{lead.country?.name || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">State</th>
                                                        <td>{lead.state?.name || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">City</th>
                                                        <td>{lead.city?.name || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Lead Queue</th>
                                                        <td>{lead.lead_queue?.name || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Assigned To</th>
                                                        <td>{lead.assigned_id?.user_name || '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Assigned At</th>
                                                        <td>{lead.assigned_at ? new Date(lead.assigned_at).toLocaleString() : '—'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className="text-muted">Converted At</th>
                                                        <td>{lead.converted_at ? new Date(lead.converted_at).toLocaleString() : '—'}</td>
                                                    </tr>
                                                    {lead.notes && (
                                                        <tr>
                                                            <th className="text-muted">Notes</th>
                                                            <td style={{ whiteSpace: 'pre-wrap' }}>{lead.notes}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Tab: Lead Interactions (Chat Style) ── */}
                    {activeTab === 'interactions' && (
                        <div className="card mb-0">
                            <div className="card-body p-0">
                                <div style={{ height: '420px', overflowY: 'auto', padding: '20px', background: '#f8f9fa' }}>
                                    {interactionLoading ? (
                                        <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                                    ) : interactions.length === 0 ? (
                                        <div className="text-center text-muted py-5">
                                            <i className="fa fa-comments fa-3x mb-3 d-block"></i>
                                            No interactions found for this lead.
                                        </div>
                                    ) : (
                                        interactions.map((item, idx) => {
                                            const isMe = item.created_by === localStorage.getItem('user_id');
                                            return (
                                                <div key={item.id || idx}
                                                    className={`d-flex mb-3 ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                                                    <div style={{
                                                        maxWidth: '70%',
                                                        background: isMe ? '#007bff' : '#fff',
                                                        color: isMe ? '#fff' : '#333',
                                                        borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                        padding: '10px 14px',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                    }}>
                                                        <div className="mb-1">
                                                            <span style={{
                                                                fontSize: '11px',
                                                                background: isMe ? 'rgba(255,255,255,0.2)' : INTERACTION_COLORS[item.interaction_type] || '#6c757d',
                                                                color: '#fff',
                                                                borderRadius: '10px',
                                                                padding: '2px 8px',
                                                            }}>
                                                                <i className={`fa fa-fw ${INTERACTION_ICONS[item.interaction_type] || 'fa-comment'} me-1`}></i>
                                                                {item.interaction_type}
                                                            </span>
                                                        </div>
                                                        {item.subject && (
                                                            <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>{item.subject}</div>
                                                        )}
                                                        <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{item.notes}</div>
                                                        {item.next_followup_at && (
                                                            <div style={{ fontSize: '11px', marginTop: '6px', opacity: 0.8 }}>
                                                                <i className="fa fa-clock me-1"></i>
                                                                Follow-up: {new Date(item.next_followup_at).toLocaleDateString()}
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.7, textAlign: 'right' }}>
                                                            {item.users?.user_name || 'Unknown'} · {new Date(item.createdAt).toLocaleString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={chatEndRef}></div>
                                </div>
                                {/* Read-only notice */}
                                <div style={{ borderTop: '1px solid #dee2e6', padding: '12px 16px', background: '#fff' }}>
                                    <small className="text-muted">
                                        <i className="fa fa-info-circle me-1"></i>
                                        These are read-only interactions from the lead.
                                        <span
                                            style={{ cursor: 'pointer', color: '#007bff', marginLeft: '6px' }}
                                            onClick={() => navigate(`/lead-details/${customer.lead_id}`)}
                                        >
                                            Go to Lead to add interactions →
                                        </span>
                                    </small>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Tab: Assignment Logs ── */}
                    {activeTab === 'assignment' && (
                        <div className="card mb-0">
                            <div className="card-body">
                                {assignLoading ? (
                                    <div className="text-center py-4"><div className="spinner-border text-primary"></div></div>
                                ) : assignLogs.length === 0 ? (
                                    <div className="text-center text-muted py-5">
                                        <i className="fa fa-history fa-3x mb-3 d-block"></i>
                                        No assignment history found.
                                    </div>
                                ) : (
                                    <div className="timeline" style={{ position: 'relative', paddingLeft: '30px' }}>
                                        <div style={{ position: 'absolute', left: '10px', top: 0, bottom: 0, width: '2px', background: '#dee2e6' }}></div>
                                        {assignLogs.map((log, idx) => (
                                            <div key={log.id || idx} style={{ position: 'relative', marginBottom: '24px' }}>
                                                <div style={{
                                                    position: 'absolute', left: '-24px', top: '4px',
                                                    width: '12px', height: '12px', borderRadius: '50%',
                                                    background: '#007bff', border: '2px solid #fff', boxShadow: '0 0 0 2px #007bff'
                                                }}></div>
                                                <div className="card border-0 shadow-sm">
                                                    <div className="card-body py-2 px-3">
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div>
                                                                <span className="fw-semibold">
                                                                    <i className="fa fa-user me-1 text-primary"></i>
                                                                    {log.users_assigned?.user_name || 'Unknown'}
                                                                </span>
                                                                <span className="text-muted mx-2">assigned by</span>
                                                                <span className="fw-semibold">{log.user_assigned_by?.user_name || 'Unknown'}</span>
                                                            </div>
                                                            <small className="text-muted">{log.assigned_at ? new Date(log.assigned_at).toLocaleString() : new Date(log.createdAt).toLocaleString()}</small>
                                                        </div>
                                                        {log.note && <p className="mb-0 mt-1 text-muted small" style={{ whiteSpace: 'pre-wrap' }}>{log.note}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Link to full lead */}
                                <div className="mt-3 text-end">
                                    <button className="btn btn-sm btn-outline-primary"
                                        onClick={() => navigate(`/lead-details/${customer.lead_id}`)}>
                                        <i className="fa fa-external-link-alt me-1"></i> View Full Lead
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
                <Footer />
            </div>
        </div>
    );
}

export default CustomerDetails;